import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '../../../../_common/functions'
import { useExperimentSelector } from '../../../../store/selectors/experimentSelector'
import { AgGridReact } from 'ag-grid-react'
import { useFpLoader } from '../../../../store/api/hooks/fpLoaderHook'

import CustomAgGrid from '../../../../components/CustomAgGrid'
import Button from '../../../../elements/button'
import i18n from '../../../../i18n'
import { cellRenderer } from '../../../../components/cellRenderer'
import P from '../../../../elements/Typo/P'
import { useMeasurementConfigure } from '../../../../_common/hook/useMeasurementConfigure'

interface Props {
    tabledata: any
    literal: string
    onContinue: () => void
    onPrevious: () => void
}

const MTEMeasurementDataPreview: React.FC<Props> = ({ tabledata, literal, onContinue, onPrevious }) => {
    const { t } = useTranslation()
    const experiment = useExperimentSelector()
    const { showLoader, hideLoader } = useFpLoader()

    const [KPIRowData, setKPIRowData] = useState<[] | null>(null)
    const [KPIColumnDefs, setKPIColumnDefs] = useState<[]>([])
    const [InterventionRowData, setInterventionRowData] = useState<[] | null>(null)
    const [InterventionColumnDefs, setInterventionColumnDefs] = useState<[]>([])
    const measurementHookFunction = useMeasurementConfigure({ experimentType: 'RCT' })
    const dataTableGrid = useRef<AgGridReact>(null)

    const createColumnDes = (data: any) => {
        return data.map((key: any, i: number) => {
            return {
                headerName: key,
                field: key,
                filter: false,
                sortable: false,
                suppressMenu: true,
                cellRenderer,
            }
        })
    }

    const getTableData = (type: 'KPI' | 'Intervention') => {
        return new Promise((resolve, reject) => {
            measurementHookFunction
                .getTableData(experiment, type)
                .then((data: any) => {
                    const columnDefs: any = createColumnDes(Object.keys(data[0]))
                    if (type === 'KPI') {
                        setKPIColumnDefs(columnDefs)
                        setKPIRowData(data)
                    } else if (type === 'Intervention') {
                        setInterventionColumnDefs(columnDefs)
                        setInterventionRowData(data)
                    }
                    resolve(data)
                })
                .catch((error: any) => {
                    reject(new Error(error?.message || 'An unknown error occurred'))
                })
        })
    }

    useEffect(() => {
        if (
            (experiment?.data_mappings?.delta_measurement_table_name || experiment?.data_mappings?.user_created_measurement_table_name) &&
            (experiment?.data_mappings?.delta_intervention_table_name || experiment?.data_mappings?.user_created_intervention_table_name)
        ) {
            showLoader()
            const data: any = Promise.all([getTableData('KPI'), getTableData('Intervention')])
            data.then((res: any) => {
                // Handle the resolved data if needed
                hideLoader()
            }).catch((error: any) => {
                // Handle the error if needed
                hideLoader()
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [experiment])

    return (
        <div>
            <div className="flex items-center justify-between">
                <div className="relative flex items-center gap-2">
                    <div className="absolute -left-5 top-0 h-full w-2 bg-primary rounded-e-md"></div>
                    <P className="!mb-0 font-semibold">{experiment?.experiment_name}</P>
                </div>
            </div>
            <p className="font-bold text-start text-[1.125rem] leading-8">{t('Measurement Data Preview')}</p>
            <p className="mt-2 text-[1rem] font-bold">{t('KPI data')}</p>

            <div className="my-4">
                <CustomAgGrid
                    key={'mteM1_' + i18n.language}
                    gridRef={dataTableGrid}
                    rowData={KPIRowData}
                    columnDefs={KPIColumnDefs}
                    defaultColDef={{ sortable: true, filter: true }}
                    theme="light"
                    pagination={false}
                    paginationPageSize={5} // Must match one of the values below
                    paginationPageSizeSelector={[5, 10, 20, 50]} // Ensure 5 is included
                    style={{ height: 350, width: '100%' }}
                    gridId="summmyGrid"
                    rowHeight={50}
                    alternativeRowColor={'#E5B61112'}
                />
            </div>

            <p className="mt-4 text-[1rem] font-bold">{t('Intervention Data:')}</p>

            <div className="mt-2">
                <CustomAgGrid
                    key={'mteM2_' + i18n.language}
                    gridRef={dataTableGrid}
                    rowData={InterventionRowData}
                    columnDefs={InterventionColumnDefs}
                    defaultColDef={{ sortable: true, filter: true }}
                    theme="light"
                    pagination={false}
                    paginationPageSize={5} // Must match one of the values below
                    paginationPageSizeSelector={[5, 10, 20, 50]} // Ensure 5 is included
                    style={{ height: 350, width: '100%' }}
                    gridId="summmyGrid"
                    rowHeight={50}
                    alternativeRowColor={'#E5B61112'}
                />
            </div>

            <div className="flex items-center justify-between gap-3 pt-4">
                <Button
                    theme="secondary"
                    size="xs"
                    styleType="outline"
                    onClick={() => {
                        trackEvent('measurement_mte_preview_previous_clicked', { experiment_literal: experiment?.experiment_literal })
                        if (onPrevious) {
                            onPrevious()
                        }
                    }}
                >
                    {t('Previous Step')}
                </Button>
                <Button
                    theme="secondary"
                    size="xs"
                    onClick={() => {
                        trackEvent('measurement_mte_preview_configure_clicked', { experiment_literal: experiment?.experiment_literal })
                        if (onContinue) {
                            onContinue()
                        }
                    }}
                >
                    {t('Configure')}
                </Button>
            </div>
        </div>
    )
}

export default MTEMeasurementDataPreview
